import { ref, computed } from 'vue';
import axios from 'axios';

/**
 * Auth composable for the classify instance. Mirrors the BFF pattern:
 * tokens live in the backend session, the SPA only knows authenticated/not.
 *
 * - `authEnabled` reflects the server-injected `window.__SERVER_CONFIG__`.
 *   It is true only on the classify deployment when the IAM client is wired,
 *   so guards and UI surfaces no-op on uscensus / tjxeproxy / local-without-config.
 * - `bootstrap()` is called once by the router before the first navigation.
 * - `login()` / `logout()` are full-page redirects through the backend so the
 *   PKCE/state and refresh-token cookies are handled server-side.
 */

interface MeResponse {
  authenticated: boolean;
  username?: string;
  profileId?: string;
}

const authEnabled = Boolean(window.__SERVER_CONFIG__?.authEnabled);
const isAuthenticated = ref(false);
const username = ref<string>('');
const profileId = ref<string>('');
const isReady = ref(false);

let bootstrapPromise: Promise<void> | null = null;

async function bootstrap(): Promise<void> {
  if (!authEnabled) {
    isReady.value = true;
    return;
  }
  if (bootstrapPromise) {
    return bootstrapPromise;
  }
  bootstrapPromise = (async () => {
    try {
      const response = await axios.get<MeResponse>('/ui/auth/me', {
        withCredentials: true,
      });
      isAuthenticated.value = Boolean(response.data?.authenticated);
      username.value = response.data?.username || '';
      profileId.value = response.data?.profileId || '';
    } catch (err) {
      console.warn('Auth bootstrap failed:', err);
      isAuthenticated.value = false;
    } finally {
      isReady.value = true;
    }
  })();
  return bootstrapPromise;
}

function login(returnTo?: string): void {
  const target = returnTo || window.location.pathname + window.location.search + window.location.hash;
  const params = new URLSearchParams();
  if (target) params.set('returnTo', target);
  const qs = params.toString();
  window.location.href = `/ui/auth/login${qs ? `?${qs}` : ''}`;
}

function logout(): void {
  window.location.href = '/ui/auth/logout';
}

export function useAuth() {
  return {
    authEnabled,
    isAuthenticated: computed(() => isAuthenticated.value),
    username: computed(() => username.value),
    profileId: computed(() => profileId.value),
    isReady: computed(() => isReady.value),
    bootstrap,
    login,
    logout,
  };
}
